"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ResponsivePopover } from "@wtc/ui";
import { Badge } from "@/components/ui/badge";
import { Fingerprint, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useUpdateCompany } from "@/features/companies/hooks/companies-mutations";
import {
  parseTechnicalIdentifiers,
  upsertTechnicalIdentifier,
  removeTechnicalIdentifier,
  technicalIdentifierSchema,
  type TechnicalIdentifier,
} from "@/lib/companies/technical-identifiers";
import {
  TechnicalIdentifiersEditorPanel,
  type TechnicalIdentifierDraft,
} from "./TechnicalIdentifiersEditorPanel";

/** Minimal company shape the editor needs (mirrors EmailAliasesEditable). */
export interface TechnicalIdentifiersEditable {
  id: string;
  name?: string | null;
  technical_identifiers?: unknown;
}

const MAX_ITEMS = 50;
const EMPTY_DRAFT: TechnicalIdentifierDraft = { system: "", label: "", value: "", notes: "" };

interface TechnicalIdentifiersEditorProps {
  company: TechnicalIdentifiersEditable;
  onUpdate?: (next: TechnicalIdentifier[]) => void;
  isEditing?: boolean;
  onEditChange?: (isEditing: boolean) => void;
  /** Render the panel inline (create/edit form context) instead of a popover. */
  showEditorDirectly?: boolean;
  /** In form context, don't hit the DB — just bubble changes up via onUpdate. */
  readOnlyMode?: boolean;
}

export function TechnicalIdentifiersEditor({
  company,
  onUpdate,
  isEditing = false,
  onEditChange,
  showEditorDirectly = false,
  readOnlyMode = false,
}: TechnicalIdentifiersEditorProps) {
  const [isOpen, setIsOpen] = useState(isEditing || showEditorDirectly);
  const [draft, setDraft] = useState<TechnicalIdentifierDraft>(EMPTY_DRAFT);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localEntries, setLocalEntries] = useState<TechnicalIdentifier[]>(
    parseTechnicalIdentifiers(company.technical_identifiers),
  );
  const updateCompany = useUpdateCompany();

  useEffect(() => {
    setLocalEntries(parseTechnicalIdentifiers(company.technical_identifiers));
  }, [company.id, company.technical_identifiers]);

  const entries = useMemo(() => localEntries, [localEntries]);

  const persist = useCallback(
    async (next: TechnicalIdentifier[], successMsg: string) => {
      const previous = entries;
      setLocalEntries(next);
      try {
        if (!readOnlyMode) {
          await updateCompany.mutateAsync({
            id: company.id,
            updates: { technical_identifiers: next },
          });
        }
        onUpdate?.(next);
        toast.success(successMsg);
      } catch (error) {
        setLocalEntries(previous);
        toast.error(
          error instanceof Error ? error.message : "Failed to save technical identifier",
        );
      }
    },
    [entries, readOnlyMode, updateCompany, company.id, onUpdate],
  );

  const handleDraftChange = useCallback((patch: Partial<TechnicalIdentifierDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setValidationError(null);
  }, []);

  const handleAdd = useCallback(() => {
    const parsed = technicalIdentifierSchema.safeParse({
      system: draft.system,
      label: draft.label,
      value: draft.value,
      notes: draft.notes || undefined,
    });
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? "Invalid identifier");
      return;
    }

    const { next } = upsertTechnicalIdentifier(entries, parsed.data);
    setDraft(EMPTY_DRAFT);
    void persist(next, "Technical identifier saved");
  }, [draft, entries, persist]);

  const handleRemove = useCallback(
    (system: string, label: string) => {
      const { next, removed } = removeTechnicalIdentifier(entries, system, label);
      if (removed === 0) return;
      void persist(next, "Technical identifier removed");
    },
    [entries, persist],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onEditChange?.(false);
  }, [onEditChange]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onEditChange?.(open);
    },
    [onEditChange],
  );

  const panelProps = {
    showEditorDirectly,
    entries,
    draft,
    onDraftChange: handleDraftChange,
    validationError,
    isSaving: updateCompany.isPending,
    maxItems: MAX_ITEMS,
    handleAdd,
    handleRemove,
    handleClose,
  };

  if (showEditorDirectly) {
    return (
      <div className="space-y-4">
        <TechnicalIdentifiersEditorPanel {...panelProps} />
      </div>
    );
  }

  return (
    <ResponsivePopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      content={<TechnicalIdentifiersEditorPanel {...panelProps} />}
      popoverSide="bottom"
      align="start"
      className="w-[26rem]"
    >
      <div className="flex items-center gap-1 flex-wrap max-w-full overflow-hidden group relative">
        {entries.length > 0 ? (
          <Badge
            variant="default"
            className="bg-blue-100 text-blue-800 text-xs py-0.5 px-2 truncate dark:bg-blue-900/40 dark:text-blue-200"
          >
            <Fingerprint className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
            <span className="truncate">
              {entries.length} identifier{entries.length === 1 ? "" : "s"}
            </span>
          </Badge>
        ) : (
          <span className="text-xs text-gray-400">None</span>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
            onEditChange?.(true);
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 hover:bg-blue-50 rounded hover:shadow-sm text-gray-400 hover:text-blue-600 dark:hover:bg-blue-950/40"
          title="Click to edit technical identifiers"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    </ResponsivePopover>
  );
}
